/**
 * Copyright © 2026 Delicious AI, LLC
 */

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material'
import React from 'react'
import { Helmet } from 'react-helmet-async'

import { ArrowBack } from '@mui/icons-material'
import type { ReviewItem } from '@/graphql'
import useT from '@/i18n/useT'

import Classification from './Classification'
import Correction from './Correction'
import Detection from './Detection'
import ReviewPagerControls from './review/ReviewPagerControls'
import ReviewSearchControls from './review/ReviewSearchControls'
import ReviewVerificationChips from './review/ReviewVerificationChips'
import ReviewWorkflowTabs from './review/ReviewWorkflowTabs'
import UnverifyFeedbackDialog from './review/UnverifyFeedbackDialog'
import { useReviewController } from './review/useReviewController'

function Review() {
  const { t } = useT()
  const {
    activeItem,
    activeItemMetadata,
    activeReviewEdit,
    activeUser,
    activeWorkflow,
    email,
    endDate,
    hasActiveReview,
    handleApplyFilters,
    handleCancelReviewEdit,
    handleEdit,
    handleModeChange,
    handleNextUserItem,
    handleObjectSearch,
    handleObjectWorkflowChange,
    handlePreviousUserItem,
    handleResetReview,
    handleUnverify,
    handleReviewEditSaved,
    handleUserSearch,
    handleUserWorkflowChange,
    infoMessage,
    loadingEdit,
    loadingUnverify,
    loadingActiveUserWorkflow,
    loadingInitialUserSearch,
    loadingObjectSearch,
    loadingUserSearch,
    mode,
    objectId,
    queryError,
    setEmail,
    setEndDate,
    setObjectId,
    setSnackbarMessage,
    setStartDate,
    snackbarMessage,
    snackbarSeverity,
    startDate,
    userItem,
    userItemState,
    userWorkflow,
    workflowCounts,
    workflowDisabled,
  } = useReviewController()
  const [unverifyItem, setUnverifyItem] = React.useState<ReviewItem | null>(
    null,
  )
  const loadingUnverifyTransition =
    loadingUnverify || loadingActiveUserWorkflow || loadingObjectSearch

  const unverifySubjectLabel = React.useMemo(() => {
    if (!unverifyItem) return t('review.unverifyFeedback.item', 'item')
    if (unverifyItem.__typename === 'DetectionReviewItem') {
      return t('review.unverifyFeedback.detectionImage', 'detection image')
    }
    if (unverifyItem.__typename === 'ClassificationReviewItem') {
      return t(
        'review.unverifyFeedback.classificationBatch',
        'classification batch',
      )
    }
    return t('review.unverifyFeedback.correctionCrop', 'correction crop')
  }, [t, unverifyItem])

  const renderVerificationContent = () => {
    if (
      !activeItem ||
      !activeItemMetadata ||
      activeItem.__typename !== 'DetectionReviewItem'
    ) {
      return null
    }

    return (
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        flexWrap="wrap"
        alignItems="center"
        sx={{ minWidth: 0, maxWidth: '100%' }}
      >
        <ReviewVerificationChips
          isVerified={activeItemMetadata.isVerified}
          verifiedByEmail={activeItemMetadata.verifiedByEmail}
          verifiedAt={activeItemMetadata.verifiedAt}
        />
      </Stack>
    )
  }

  const renderItemView = () => {
    const detectionExternalChromeHeight =
      !activeReviewEdit && mode === 'BY_USER' && !!activeUser ? 144 : 0

    if (activeReviewEdit?.workflow === 'DETECTION') {
      return (
        <Detection
          initialSession={activeReviewEdit.session}
          hideInternalNavigation
          externalUIHeight={detectionExternalChromeHeight}
          onApproveSuccess={handleReviewEditSaved}
          reviewSidebarContent={renderVerificationContent()}
        />
      )
    }

    if (activeReviewEdit?.workflow === 'CLASSIFICATION') {
      return (
        <Classification
          initialBatch={activeReviewEdit.batch}
          reviewMetadata={activeReviewEdit.item.reviewMetadata}
          onSubmitSuccess={handleReviewEditSaved}
        />
      )
    }

    if (activeReviewEdit?.workflow === 'CORRECTION') {
      return (
        <Correction
          initialCorrection={activeReviewEdit.correction}
          reviewMetadata={activeReviewEdit.item.reviewMetadata}
          onSubmitSuccess={handleReviewEditSaved}
        />
      )
    }

    if (!activeItem) return null

    if (activeItem.__typename === 'DetectionReviewItem') {
      return (
        <Detection
          initialSession={activeItem.session}
          hideInternalNavigation
          externalUIHeight={detectionExternalChromeHeight}
          reviewSidebarContent={renderVerificationContent()}
        />
      )
    }

    if (activeItem.__typename === 'ClassificationReviewItem') {
      return (
        <Classification
          initialBatch={activeItem.batch}
          readOnly
          reviewMetadata={activeItem.reviewMetadata}
        />
      )
    }

    if (activeItem.__typename === 'CorrectionReviewItem') {
      return (
        <Correction
          initialCorrection={activeItem.correction}
          reviewMetadata={activeItem.reviewMetadata}
          readOnly
        />
      )
    }

    return null
  }

  return (
    <>
      <Helmet title={t('review.helmet', 'Review')} />
      <Stack spacing={2}>
        {!hasActiveReview ? (
          <>
            <Tabs
              value={mode}
              onChange={handleModeChange}
              textColor="primary"
              indicatorColor="primary"
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab value="BY_USER" label={t('review.tabs.byUser', 'By User')} />
              <Tab
                value="BY_OBJECT"
                label={t('review.tabs.byObject', 'By Object')}
              />
            </Tabs>

            <ReviewWorkflowTabs
              value={activeWorkflow}
              onChange={
                mode === 'BY_USER'
                  ? handleUserWorkflowChange
                  : handleObjectWorkflowChange
              }
              counts={mode === 'BY_USER' ? workflowCounts : undefined}
              disabled={mode === 'BY_USER' ? workflowDisabled : undefined}
            />

            <ReviewSearchControls
              mode={mode}
              email={email}
              objectId={objectId}
              workflow={activeWorkflow}
              loading={
                mode === 'BY_USER' ? loadingUserSearch : loadingObjectSearch
              }
              searchingMessage={
                loadingInitialUserSearch
                  ? t(
                      'review.search.loadingUserMessage',
                      'Searching review history for this verifier...',
                    )
                  : null
              }
              onEmailChange={setEmail}
              onObjectIdChange={setObjectId}
              onSearch={
                mode === 'BY_USER' ? handleUserSearch : handleObjectSearch
              }
            />
          </>
        ) : null}

        <Stack spacing={1.5}>
          {hasActiveReview ? (
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ md: 'center' }}
            >
              <Button
                variant="text"
                startIcon={<ArrowBack />}
                onClick={handleResetReview}
                sx={{ alignSelf: 'flex-start' }}
              >
                {t('review.actions.newSearch', 'New search')}
              </Button>
              {activeReviewEdit ? (
                <Stack
                  direction="row"
                  spacing={1}
                  useFlexGap
                  flexWrap="wrap"
                  alignItems="center"
                  justifyContent="flex-end"
                >
                  <Typography variant="body2" color="text.secondary">
                    {t('review.edit.editingInline', 'Editing review item')}
                  </Typography>
                  <Button variant="outlined" onClick={handleCancelReviewEdit}>
                    {t('common.cancel', 'Cancel')}
                  </Button>
                </Stack>
              ) : activeItem && activeItemMetadata ? (
                <Stack
                  direction="row"
                  spacing={1}
                  useFlexGap
                  flexWrap="wrap"
                  alignItems="center"
                  justifyContent="flex-end"
                >
                  <Tooltip
                    title={
                      activeItemMetadata.leasedByEmail
                        ? t(
                            'review.actions.editDisabledLease',
                            'Currently being edited by {{email}}',
                            { email: activeItemMetadata.leasedByEmail },
                          )
                        : ''
                    }
                  >
                    <span>
                      <Button
                        variant="contained"
                        onClick={() => handleEdit(activeItem)}
                        disabled={
                          loadingEdit || !activeItemMetadata.canEnterEditMode
                        }
                      >
                        {t('review.actions.edit', 'Edit')}
                      </Button>
                    </span>
                  </Tooltip>
                  {activeItemMetadata.isVerified ? (
                    <Tooltip
                      title={
                        activeItemMetadata.leasedByEmail
                          ? t(
                              'review.actions.unverifyDisabledLease',
                              'Currently being edited by {{email}}',
                              { email: activeItemMetadata.leasedByEmail },
                            )
                          : ''
                      }
                    >
                      <span>
                        <Button
                          variant="outlined"
                          color="warning"
                          onClick={() => setUnverifyItem(activeItem)}
                          disabled={
                            loadingUnverify ||
                            Boolean(activeItemMetadata.leasedByEmail)
                          }
                        >
                          {t('review.actions.unverify', 'Unverify')}
                        </Button>
                      </span>
                    </Tooltip>
                  ) : null}
                </Stack>
              ) : null}
            </Stack>
          ) : null}

          {mode === 'BY_USER' && activeUser && !activeReviewEdit ? (
            <ReviewWorkflowTabs
              value={userWorkflow}
              onChange={handleUserWorkflowChange}
              counts={workflowCounts}
              disabled={workflowDisabled}
            />
          ) : null}

          {queryError ? (
            <Alert severity="error">{queryError.message}</Alert>
          ) : null}

          {mode === 'BY_USER' && activeUser && !activeReviewEdit ? (
            <ReviewPagerControls
              pager={userItem?.pager}
              startDate={startDate}
              endDate={endDate}
              displayName={
                activeUser?.firstName || activeUser?.lastName
                  ? `${activeUser?.firstName ?? ''} ${activeUser?.lastName ?? ''}`.trim()
                  : null
              }
              loading={userItemState.loading}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
              onApplyFilters={handleApplyFilters}
              onPrevious={handlePreviousUserItem}
              onNext={handleNextUserItem}
            />
          ) : null}
          {mode === 'BY_USER' && loadingActiveUserWorkflow ? (
            <Box
              sx={{
                minHeight: 280,
                display: 'grid',
                placeItems: 'center',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: 'background.paper',
                px: 3,
              }}
            >
              <Stack spacing={1.5} alignItems="center">
                <CircularProgress size={28} />
                <Typography variant="body2" color="text.secondary">
                  {t(
                    'review.loading.workflow',
                    'Loading review items for this workflow...',
                  )}
                </Typography>
              </Stack>
            </Box>
          ) : null}
          {infoMessage ? <Alert severity="info">{infoMessage}</Alert> : null}
          {loadingActiveUserWorkflow ? null : renderItemView()}
        </Stack>
      </Stack>

      <UnverifyFeedbackDialog
        open={Boolean(unverifyItem)}
        loading={loadingUnverifyTransition}
        subjectLabel={unverifySubjectLabel}
        onClose={() => setUnverifyItem(null)}
        onSubmit={async feedbackNote => {
          if (!unverifyItem) return
          const success = await handleUnverify(unverifyItem, feedbackNote)
          if (success) setUnverifyItem(null)
        }}
      />
      <Snackbar
        open={Boolean(snackbarMessage)}
        autoHideDuration={4000}
        onClose={() => setSnackbarMessage('')}
      >
        <Alert
          onClose={() => setSnackbarMessage('')}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  )
}

export default Review
